import React, { useState, useEffect } from 'react';
import { Button, Modal, Radio, Spin, message } from 'antd';
import { getVideoSetting, updateVideoSetting } from 'api/videoSetting';
import ZoomLogo from 'assets/common/zoom_logo.svg';
import Ico1 from 'assets/videoVisit/ico-1.svg';
import Ico2 from 'assets/videoVisit/ico-2.svg';
import Ico3 from 'assets/videoVisit/ico-3.svg';
import Ico4 from 'assets/videoVisit/ico-4.svg';
import s from './s.module.less';
import commonS from 'styles/common.module.less';
import ZoomMeetingLandingPage from 'components/ZoomVideoVisitsBanner/ZoomMeetingLandingPage';
import TreatmentPlanTemplate from './components/TreatmentPlanTemplate';

const VideoVisitPage = () => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showLearnMore, setShowLearnMore] = useState(false);
    const [meetingLinkGenerationType, setMeetingLinkGenerationType] = useState<'KLARITY_MEETING' | 'CUSTOM'>('KLARITY_MEETING');

    // Fetch video settings
    useEffect(() => {
        const fetchVideoSetting = async () => {
            setLoading(true);
            try {
                const result = await getVideoSetting();
                if (result?.data?.data?.meetingLinkGenerationType) {
                    setMeetingLinkGenerationType(result.data.data.meetingLinkGenerationType);
                }
            } catch (err) {
                console.error('Failed to fetch video settings:', err);
                message.error('Failed to fetch video settings. Please try again later.');
            } finally {
                setLoading(false);
            }
        };
        fetchVideoSetting();
    }, []);

    // Update video settings
    const handleRadioChange = async (e: any) => {
        const newValue = e.target.value as 'KLARITY_MEETING' | 'CUSTOM';
        setSaving(true);
        try {
            const result = await updateVideoSetting(newValue);
            if (result?.data?.code === 200) {
                setMeetingLinkGenerationType(newValue);
                message.success('Saved successfully.');
            } else {
                message.error(result?.data?.message || 'Failed to save settings. Please try again later.');
            }
        } catch (err) {
            console.error('Failed to update video settings:', err);
            message.error('Failed to save settings. Please try again later.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Spin spinning={loading}>
            <div className={s.wrap}>
                <div className={s.header}>
                    <h2 className={s.title}>Video visit</h2>
                    <Button className={s.learnMoreBtn} onClick={() => {
                        setShowLearnMore(true);
                    }}>
                        Learn more
                    </Button>
                </div>

                <div className={s.content}>
                    <div className={s.section}>
                        <p className={s.sectionTitle}>Set your default video visit platform</p>
                        <div className={s.optionsContainer}>
                            <div
                                className={`${s.optionCard} ${meetingLinkGenerationType === 'KLARITY_MEETING' ? s.selected : ''}`}
                                onClick={() => {
                                    if (meetingLinkGenerationType !== 'KLARITY_MEETING') {
                                        handleRadioChange({ target: { value: 'KLARITY_MEETING' } });
                                    }
                                }}
                            >
                                <Radio
                                    checked={meetingLinkGenerationType === 'KLARITY_MEETING'}
                                    value="KLARITY_MEETING"
                                    onChange={handleRadioChange}
                                    className={s.radio}
                                />
                                <div className={s.optionContent}>
                                    <div className={s.optionHeader}>
                                        <span className={s.optionTitle}>Klarity Meet</span>
                                        <span className={s.recommendedBadge}>Recommended</span>
                                    </div>
                                    <p className={s.optionDescription}>
                                        Automatically generate a secure video link powered by{' '}
                                        <img src={ZoomLogo} alt="Zoom" className={s.zoomLogo} />
                                    </p>
                                </div>
                            </div>

                            <div
                                className={`${s.optionCard} ${meetingLinkGenerationType === 'CUSTOM' ? s.selected : ''}`}
                                onClick={() => {
                                    if (meetingLinkGenerationType !== 'CUSTOM') {
                                        handleRadioChange({ target: { value: 'CUSTOM' } });
                                    }
                                }}
                            >
                                <Radio
                                    checked={meetingLinkGenerationType === 'CUSTOM'}
                                    value="CUSTOM"
                                    onChange={handleRadioChange}
                                    className={s.radio}
                                />
                                <div className={s.optionContent}>
                                    <p className={s.optionTitle}>Custom link</p>
                                    <p className={s.optionDescription}>
                                        Manually add a video link from your own meeting service.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={s.section}>
                        <p className={s.sectionTitle}>Explore the benefits of Klarity Meet integration</p>
                        <div className={s.benefitsContainer}>
                            <div className={s.benefitsColumn}>
                                <div className={s.benefitItem}>
                                    <div className={s.benefitIcon}>
                                        <img src={Ico1} alt="Cost savings" />
                                    </div>
                                    <div className={s.benefitText}>
                                        <p className={s.benefitTitle}>Cost savings</p>
                                        <p className={s.benefitDescription}>No additional fees for meetings.</p>
                                    </div>
                                </div>

                                <div className={s.benefitItem}>
                                    <div className={s.benefitIcon}>
                                        <img src={Ico2} alt="Patients get link automatically" />
                                    </div>
                                    <div className={s.benefitText}>
                                        <p className={s.benefitTitle}>Patients get link automatically</p>
                                        <p className={s.benefitDescription}>
                                            Video links are sent to patients by email and SMS automatically.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className={s.benefitsColumn}>
                                <div className={s.benefitItem}>
                                    <div className={s.benefitIcon}>
                                        <img src={Ico3} alt="Secure & HIPAA-compliant" />
                                    </div>
                                    <div className={s.benefitText}>
                                        <p className={s.benefitTitle}>Secure & HIPAA-compliant</p>
                                        <p className={s.benefitDescription}>
                                            Secure video visits that meet healthcare privacy standards.
                                        </p>
                                    </div>
                                </div>

                                <div className={s.benefitItem}>
                                    <div className={s.benefitIcon}>
                                        <img src={Ico4} alt="AI-powered scribing" />
                                    </div>
                                    <div className={s.benefitText}>
                                        <div className={s.benefitTitleRow}>
                                            <p className={s.benefitTitle}>AI-powered scribing</p>
                                            <span className={s.comingSoonBadge}>Coming soon</span>
                                        </div>
                                        <p className={s.benefitDescription}>
                                            Future AI note capture to help you stay focused on your patient.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <TreatmentPlanTemplate />

            <Modal
                open={showLearnMore}
                onCancel={() => {
                    setShowLearnMore(false);
                }}
                footer={null}
                width="1166px"
                className={commonS.modalFixHeightWithoutPaddingWrap}
                title={<div>Learn more about secure <img className={s.modalTitleIcon} src={ZoomLogo} /> video visits</div>}
            >
                <ZoomMeetingLandingPage
                    onGetStarted={() => { }}
                    onClose={() => {
                        setShowLearnMore(false);
                    }}
                />
            </Modal>
        </Spin>
    );
};

export default VideoVisitPage;
